<template>
  <!-- Android: banner discreto com prompt nativo -->
  <div
    v-if="showAndroidBanner"
    class="pwa-install-banner"
    data-test="pwa-install-banner"
    role="region"
    aria-label="Convite de instalação do app"
  >
    <div class="pwa-install-icon">
      <i class="fas fa-download"></i>
    </div>
    <div class="pwa-install-text">
      <strong>Instalar o SD Admin</strong>
      <span>Acesso rápido na tela inicial, em tela cheia, sem a barra do navegador.</span>
    </div>
    <div class="pwa-install-actions">
      <BaseButton variant="ghost" data-test="pwa-dismiss-btn" @click="onDismiss">
        Não
      </BaseButton>
      <BaseButton variant="ghost" data-test="pwa-defer-btn" @click="onDefer">
        Lembre-me mais tarde
      </BaseButton>
      <BaseButton
        variant="primary"
        icon="fas fa-download"
        data-test="pwa-install-btn"
        @click="onInstall"
      >
        Instalar
      </BaseButton>
    </div>
  </div>

  <!-- iOS Safari: modal em 2 passos (perguntar → guia manual; não há prompt nativo) -->
  <BaseModal v-model="showIosGuide" title="Instalar o SD Admin" size="medium">
    <template v-if="iosStep === 'ask'">
      <p class="pwa-ios-lead">
        Instale o SD Admin na tela inicial para abrir em tela cheia, com 1 toque —
        sem a barra do navegador.
      </p>
    </template>
    <template v-else>
      <p class="pwa-ios-lead">
        No iPhone/iPad a instalação é manual — leva 3 toques:
      </p>
      <IosInstallGuide />
    </template>

    <template #footer>
      <template v-if="iosStep === 'ask'">
        <BaseButton variant="ghost" data-test="pwa-dismiss-btn" @click="onDismiss">
          Não
        </BaseButton>
        <BaseButton variant="ghost" data-test="pwa-defer-btn" @click="onDefer">
          Lembre-me mais tarde
        </BaseButton>
        <BaseButton
          variant="primary"
          icon="fas fa-arrow-up-from-bracket"
          data-test="pwa-ios-how-btn"
          @click="iosStep = 'guide'"
        >
          Sim, como faço?
        </BaseButton>
      </template>
      <BaseButton
        v-else
        variant="primary"
        data-test="pwa-ios-done-btn"
        @click="onDefer"
      >
        Entendi
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script setup>
import { computed, ref } from "vue";
import { usePwaInstall } from "@/composables/usePwaInstall";
import BaseButton from "@/components/ui/BaseButton.vue";
import BaseModal from "@/components/ui/BaseModal.vue";
import IosInstallGuide from "@/components/pwa/IosInstallGuide.vue";

const {
  canPrompt,
  isIos,
  isSafari,
  shouldShowInvite,
  promptInstall,
  deferInvite,
  dismissInvite,
} = usePwaInstall();

// iOS: "ask" (pergunta) → "guide" (passo a passo, após "Sim, como faço?").
const iosStep = ref("ask");

const showAndroidBanner = computed(
  () => shouldShowInvite.value && canPrompt.value,
);

const showIosGuide = computed({
  get: () => shouldShowInvite.value && isIos && isSafari && !canPrompt.value,
  // Fechar a modal (X / overlay) = "lembre-me mais tarde".
  set: (open) => {
    if (!open) deferInvite();
  },
});

function onInstall() {
  promptInstall();
}

function onDefer() {
  deferInvite();
}

function onDismiss() {
  dismissInvite();
}
</script>

<style scoped>
.pwa-install-banner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: var(--space-4) 0 0;
  padding: var(--space-3) var(--space-4);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.pwa-install-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: var(--radius-md);
  background: var(--accent-primary);
  color: white;
}

.pwa-install-text {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.pwa-install-text span {
  color: var(--text-secondary);
  font-size: var(--font-sm);
}

.pwa-install-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
  flex-shrink: 0;
}

.pwa-ios-lead {
  margin: 0 0 var(--space-3);
  color: var(--text-secondary);
}

@media (max-width: 640px) {
  .pwa-install-banner {
    flex-wrap: wrap;
  }
  .pwa-install-actions {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
}

/* Android em telas estreitas (≤ 480px): as 3 ações não cabem numa linha —
   mesmo caso do footer do BaseModal. Stack vertical, botões full-width. */
@media (max-width: 480px) {
  .pwa-install-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .pwa-install-actions :deep(button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
